'use client';

import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { PageLayout, PageLayoutLite } from '@/modules/page-layout';
import { GlobalError } from '@/shared/components/global-error';
import { SkeletonPageLayout } from '@/shared/components/skeletons';
import { SpecialError, SpecialErrorType } from '@/shared/components/special-error';
import { FALLBACK_ERROR_CONTENT } from '@/shared/constants';
import { EdgeConfigProvider, getMetaDataByDefault, useFetchEdgeConfigByPathnameQuery } from '@/shared/modules/edge-config';
import { useCurrentLocale } from '@/shared/modules/localization';
const SITEMAP_NODE_PATHNAME = 'not-found';

/**
 * @useClient
 */
export default function NotFoundPage() {
  const locale = useCurrentLocale();

  /**
   * @todo
   * Need to create a Metadata component (from edgeConfig) that can be rendered in 'user client' components
   */
  const {
    title,
    description,
    robots,
    other
  } = getMetaDataByDefault({
    title: FALLBACK_ERROR_CONTENT.title,
    description: FALLBACK_ERROR_CONTENT.description
  });
  const errorMetadata = <>
      <title>{(title as string)}</title>
      <meta name="description" content={(description as string)} />
      <meta name="robots" content={(robots as string)} />
      <meta name="google" content={(other?.google as string)} />
    </>;
  const {
    data: edgeConfig,
    isLoading
  } = useFetchEdgeConfigByPathnameQuery({
    pathname: `/${locale}/${SITEMAP_NODE_PATHNAME}`,
    options: {
      queryKeys: [SITEMAP_NODE_PATHNAME]
    }
  });
  React.useEffect(() => {
    const error = new Error(`Request failed with status code 404 Not Found: GET ${window.location.href}`);
    Sentry.captureException(error);
    console.group('[NotFoundPage]');
    console.log(`error.message: ${error?.message}`);
    console.groupEnd();
  }, []);
  if (isLoading) {
    return <SkeletonPageLayout />;
  }
  if (edgeConfig === undefined) {
    return <>
        {errorMetadata}

        <PageLayoutLite>
          <GlobalError title={FALLBACK_ERROR_CONTENT.title} description={FALLBACK_ERROR_CONTENT.description} $hasPaddingTop />
        </PageLayoutLite>
      </>;
  }
  return <>
      {errorMetadata}

      <EdgeConfigProvider edgeConfig={edgeConfig} shouldConfigBePassedToGlobal={true} data-sentry-element="EdgeConfigProvider" data-sentry-source-file="not-found.tsx">
        <PageLayout data-sentry-element="PageLayout" data-sentry-source-file="not-found.tsx">
          <SpecialError errorType={SpecialErrorType.NotFound} data-sentry-element="SpecialError" data-sentry-source-file="not-found.tsx" />
        </PageLayout>
      </EdgeConfigProvider>
    </>;
}